import {
    defaultHashSha256,
    INTERNAL_STORAGE_NAME,
    type HashFunction,
    type RxStorage
} from 'rxdb';
import { defineStoredJson } from '../utils/storage';

/**
 * What `addCollections` does for all collections at once, after the
 * collections' IndexedDBs are open and before any of them is ready.
 *
 * openProfile times each collection; this times the shared part in between: the
 * schema checksums and the writes to RxDB's internal store (the storage token,
 * one bookkeeping document per collection). A report that has every collection
 * "waiting on RxDB" is stuck in one of these — this says which.
 *
 * Kept like openProfile: in sessionStorage, the previous open alongside.
 */

/** Milliseconds since the document started loading. */
export interface InternalStoreCall {
    method: string;
    begun: number;
    done?: number;
    failed?: boolean;
}

export interface OpenSteps {
    /** Calls on the internal store, in the order they began. */
    internal: InternalStoreCall[];
    hashes: { requested: number; done: number; longest: number };
}

const emptySteps = (): OpenSteps => ({
    internal: [],
    hashes: { requested: 0, done: 0, longest: 0 }
});

function defineSteps(key: string) {
    return defineStoredJson<OpenSteps>({
        key,
        area: 'session',
        fallback: emptySteps,
        decode: (parsed) =>
            parsed !== null &&
            typeof parsed === 'object' &&
            'internal' in parsed &&
            'hashes' in parsed
                ? (parsed as OpenSteps)
                : undefined
    });
}

const currentSteps = defineSteps('db_open_steps');
const previousSteps = defineSteps('db_open_steps_previous');

export function readOpenSteps(): OpenSteps {
    return currentSteps.read();
}

export function readPreviousOpenSteps(): OpenSteps {
    return previousSteps.read();
}

/** Only while the open runs; afterwards the internal store is busy for good. */
let recording = false;

/**
 * A runaway loop must not fill sessionStorage. A normal open makes two writes
 * and one read per collection.
 */
const MAX_CALLS = 100;

export function beginOpenSteps(): void {
    const last = currentSteps.read();
    if (last.internal.length > 0 || last.hashes.requested > 0) {
        previousSteps.write(last);
    }
    currentSteps.clear();
    recording = true;
}

export function endOpenSteps(): void {
    recording = false;
}

function update(change: (steps: OpenSteps) => void) {
    const steps = currentSteps.read();
    change(steps);
    currentSteps.write(steps);
}

function timeInternalCall<T>(method: string, call: () => Promise<T>) {
    if (!recording) {
        return call();
    }
    let index = -1;
    update((steps) => {
        if (steps.internal.length < MAX_CALLS) {
            index = steps.internal.length;
            steps.internal.push({ method, begun: performance.now() });
        }
    });
    const settle = (failed: boolean) => {
        if (index < 0) {
            return;
        }
        update((steps) => {
            const entry = steps.internal[index];
            if (entry) {
                entry.done = performance.now();
                if (failed) {
                    entry.failed = true;
                }
            }
        });
    };
    const result = call();
    void result.then(
        () => settle(false),
        () => settle(true)
    );
    return result;
}

const TIMED_METHODS = [
    'bulkWrite',
    'findDocumentsById',
    'query',
    'count'
] as const;

/**
 * The storage, with the internal store's reads and writes timed. Collection
 * stores are handed back untouched — their opens are openProfile's business.
 */
export function profiledStorage<Internals, Options>(
    storage: RxStorage<Internals, Options>
): RxStorage<Internals, Options> {
    return {
        name: storage.name,
        rxdbVersion: storage.rxdbVersion,
        async createStorageInstance(params) {
            const instance = await storage.createStorageInstance(params);
            if (params.collectionName !== INTERNAL_STORAGE_NAME) {
                return instance;
            }
            for (const method of TIMED_METHODS) {
                const original = instance[method].bind(instance) as (
                    ...args: unknown[]
                ) => Promise<unknown>;
                Object.assign(instance, {
                    [method]: (...args: unknown[]) =>
                        timeInternalCall(method, () => original(...args))
                });
            }
            return instance;
        }
    };
}

/** RxDB's own checksum, counted: every schema is hashed before any write. */
export const profiledHashFunction: HashFunction = (input) => {
    if (!recording) {
        return defaultHashSha256(input);
    }
    const begun = performance.now();
    update((steps) => {
        steps.hashes.requested += 1;
    });
    const result = defaultHashSha256(input);
    void result.then(
        () =>
            update((steps) => {
                steps.hashes.done += 1;
                steps.hashes.longest = Math.max(
                    steps.hashes.longest,
                    performance.now() - begun
                );
            }),
        () => {}
    );
    return result;
};

/**
 * One method's calls in one phrase — an open reads once per collection, and
 * two dozen ranges in a row would hide the one call that matters:
 *
 *     findDocumentsById 26× 564–573 ms
 *     bulkWrite 2× 355–525 ms, 1 offen seit 360 ms (läuft seit 25000 ms)
 */
function formatMethodCalls(
    method: string,
    calls: InternalStoreCall[],
    now: number | undefined
): string {
    const settled = calls.filter((call) => call.done !== undefined);
    const pending = calls.filter((call) => call.done === undefined);
    const failed = settled.filter((call) => call.failed).length;
    let phrase = method + (calls.length > 1 ? ` ${calls.length}×` : '');
    if (settled.length > 0) {
        const begun = Math.min(...settled.map((call) => call.begun));
        const done = Math.max(...settled.map((call) => call.done ?? 0));
        phrase += ` ${Math.round(begun)}–${Math.round(done)} ms`;
    }
    if (failed > 0) {
        phrase +=
            calls.length > 1
                ? ` (${failed} fehlgeschlagen)`
                : ' (fehlgeschlagen)';
    }
    if (pending.length > 0) {
        const begun = Math.min(...pending.map((call) => call.begun));
        phrase +=
            (calls.length > 1 ? `, ${pending.length} offen seit` : ' seit') +
            ` ${Math.round(begun)} ms` +
            (now === undefined
                ? ''
                : ` (läuft seit ${Math.round(now - begun)} ms)`);
    }
    return phrase;
}

/**
 *     Interner Speicher: bulkWrite 2× 355–562 ms, findDocumentsById 26× 564–573 ms
 *     Prüfsummen: 26 von 26 fertig, längste 3 ms
 */
export function formatOpenSteps(
    steps: OpenSteps,
    /** This document's clock; leave out for a previous document's steps. */
    now?: number
): string[] {
    const lines: string[] = [];
    if (steps.internal.length > 0) {
        // In the order each method was first called.
        const byMethod = new Map<string, InternalStoreCall[]>();
        for (const call of steps.internal) {
            byMethod.set(call.method, [
                ...(byMethod.get(call.method) ?? []),
                call
            ]);
        }
        const phrases = [...byMethod].map(([method, calls]) =>
            formatMethodCalls(method, calls, now)
        );
        lines.push(`Interner Speicher: ${phrases.join(', ')}`);
    }
    const { hashes } = steps;
    if (hashes.requested > 0) {
        lines.push(
            `Prüfsummen: ${hashes.done} von ${hashes.requested} fertig, längste ${Math.round(hashes.longest)} ms`
        );
    }
    return lines;
}
